import { useSavedBrowserOpenIntent } from "@/shared/useSavedBrowserTasks.ts";
import { BrowserControlDialog } from "@/components/messages/browserTakeover/BrowserControlDialog.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn, getApiOrigin } from "@/lib/utils.ts";
import {
  browserRestartCopy,
  browserTakeoverCopy,
  takeoverTimeLeft,
  isBrowserWindowUrl,
} from "@/shared/browserTakeover.ts";
import { isCancellation } from "@/shared/retryPolicy.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import { Globe, LoaderCircle, MousePointerClick, MoreHorizontal, LockKeyhole } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useBrowserTakeover } from "./useBrowserTakeover.ts";
import { BrowserLoginForm } from "@/components/connectors/BrowserLoginForm.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";

/** Under five minutes, the time left reads as a warning: the browser closes at zero. */
const EXPIRY_WARNING_MS = 5 * 60_000;

/** The current time, ticking every 30 s while `active`, since the bar counts whole minutes. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const tick = () => setNow(Date.now());
    // `now` dates from mount, which can be long before the hold began, so read the clock
    // again straight away rather than on the first 30 s tick.
    const first = setTimeout(tick, 0);
    const timer = setInterval(tick, 30_000);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [active]);
  return now;
}

/**
 * Above the composer while the agent's browser can change hands. While okthx browses it
 * offers to take control; while okthx stops it offers to cancel; while the user holds the
 * browser it opens it, hands it back or ends the task. Private sign-in places the same
 * control inside its composer card.
 *
 * The live view link is fetched each time the browser opens and held in state only, like
 * the computer desktop's, tied to the hold it was minted for: closing the dialog or ending
 * the hold here drops it, and a hold that ends elsewhere closes the dialog.
 */
export const BrowserTakeoverBar = ({
  thread,
  signInSlot,
}: {
  thread: GetThreadResp;
  signInSlot?: { artifactId: string; container: HTMLElement };
}) => {
  const {
    status,
    take,
    handBack,
    end,
    endTask,
    liveView,
    watch,
    connectionLog,
    restart,
    refresh,
    cancelConnection,
  } = useBrowserTakeover(thread);
  // The open browser, tied to the hold it was minted for. Once that hold ends (handed back,
  // expired or ended elsewhere), nothing shows it.
  const [openView, setOpenView] = useState<{
    taskId: string;
    controlId?: string;
    url: string;
  } | null>(null);
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const [openingTaskId, setOpeningTaskId] = useState<string>();
  const [openingFailure, setOpeningFailure] = useState<{ taskId: string; controlId?: string }>();
  const openAttempt = useRef(0);
  // A takeover asked for on this screen opens as soon as it is granted. One granted to
  // another device waits for "Open browser".
  const openWhenGranted = useRef<string | null>(null);
  const heldControlId = status?.state === "user" ? status.controlId : undefined;
  const [manualControlId, setManualControlId] = useState<string>();
  const openedCredentialControl = useRef<string | undefined>(undefined);
  const credentialRequest = status?.state === "user" ? status.credentialRequest : undefined;
  const showSignIn = !!credentialRequest && !!heldControlId && manualControlId !== heldControlId;

  const { intent: savedOpenIntent, consume: consumeSavedOpen } = useSavedBrowserOpenIntent(
    thread.id,
  );
  const liveViewUrl = openView !== null && openView.taskId === status?.taskId ? openView.url : null;
  const now = useNow(heldControlId !== undefined);
  // The expiry job says when it releases the browser, but a missed event would leave the
  // hold on screen. At its deadline, ask the server instead.
  const pastDeadline =
    heldControlId !== undefined && status?.expiresAtMs !== undefined && status.expiresAtMs <= now;
  useEffect(() => {
    if (pastDeadline) void refresh();
  }, [pastDeadline, refresh]);

  const { mutate: mintLiveView, reset: resetLiveView } = liveView;
  const taskId = status?.taskId;
  const currentHold = useRef({ taskId, controlId: heldControlId });
  useLayoutEffect(() => {
    currentHold.current = { taskId, controlId: heldControlId };
  }, [taskId, heldControlId]);
  const openBrowser = useCallback(
    (controlId: string) => {
      const attempt = ++openAttempt.current;
      setOpenView(null);
      setOpeningTaskId(taskId);
      setOpeningFailure(undefined);
      mintLiveView(controlId, {
        onSuccess: (url) => {
          if (attempt === openAttempt.current && taskId) setOpenView({ taskId, controlId, url });
        },
        // A cancelled lookup is not a failed one, so leave the failure state alone and let
        // `onSettled` clear the opening state. The dialog then closes rather than accusing
        // the browser of being unreachable.
        onError: (error) => {
          if (attempt === openAttempt.current && taskId && !isCancellation(error))
            setOpeningFailure({ taskId, controlId });
        },
        onSettled: () => {
          if (attempt === openAttempt.current) setOpeningTaskId(undefined);
        },
      });
    },
    [mintLiveView, taskId],
  );
  useEffect(() => {
    if (!credentialRequest || !heldControlId || openedCredentialControl.current === heldControlId)
      return;
    openedCredentialControl.current = heldControlId;
    openWhenGranted.current = null;
    openBrowser(heldControlId);
  }, [credentialRequest, heldControlId, openBrowser]);

  useEffect(() => {
    if (heldControlId === undefined || heldControlId !== openWhenGranted.current) return;
    openWhenGranted.current = null;
    openBrowser(heldControlId);
  }, [heldControlId, openBrowser]);

  useEffect(() => {
    if (!heldControlId || !savedOpenIntent || savedOpenIntent.taskId !== status?.taskId) return;
    consumeSavedOpen();
    if (Date.now() - savedOpenIntent.requestedAtMs < 5 * 60_000) openBrowser(heldControlId);
  }, [heldControlId, savedOpenIntent, status?.taskId, consumeSavedOpen, openBrowser]);

  const closeBrowser = () => {
    openAttempt.current++;
    cancelConnection();
    openWhenGranted.current = null;
    setOpeningTaskId(undefined);
    setOpeningFailure(undefined);
    setOpenView(null);
    // The mutation keeps its last result too; drop the link there as well.
    resetLiveView();
    watch.reset();
  };
  const openWatch = (taskId: string) => {
    const attempt = ++openAttempt.current;
    setOpeningTaskId(taskId);
    setOpeningFailure(undefined);
    watch.mutate(taskId, {
      onSuccess: (url) => {
        if (attempt === openAttempt.current) setOpenView({ taskId, url });
      },
      onError: (error) => {
        if (attempt === openAttempt.current && !isCancellation(error))
          setOpeningFailure({ taskId });
      },
      onSettled: () => {
        if (attempt === openAttempt.current) setOpeningTaskId(undefined);
      },
    });
  };

  // A hold that ended elsewhere (handed back, expired, ended) drops its link. The state is
  // adjusted during render, as for any value derived from props; the mutation's copy goes
  // in an effect, since resetting it notifies an external store.
  if (
    openView !== null &&
    (status?.taskId !== openView.taskId ||
      (openView.controlId !== undefined &&
        openView.controlId !== heldControlId &&
        !isBrowserWindowUrl(openView.url, getApiOrigin())))
  )
    setOpenView(null);
  useEffect(() => {
    if (heldControlId === undefined) resetLiveView();
  }, [heldControlId, resetLiveView]);

  if (!status) return null;
  // A released browser whose request is still restartable shows the restart offer rather than
  // nothing, because the thread's hold message promised a browser.
  if (status.state === "unavailable") {
    const target = status.restart;
    if (!target) return null;
    return (
      <div className="pointer-events-auto flex justify-center mb-2" role="status">
        <div className="flex flex-wrap items-center gap-2 w-[688px] max-w-full rounded-2xl border bg-popover px-3 py-2 text-xs">
          <Globe className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 text-muted-foreground">{browserRestartCopy.message}</span>
          <Button
            type="button"
            size="xs"
            variant="primary"
            data-statsig-name="browser-takeover-restart"
            disabled={restart.isPending}
            onClick={() => restart.mutate(target)}
          >
            {restart.isPending ? browserRestartCopy.pendingLabel : browserRestartCopy.label}
          </Button>
        </div>
      </div>
    );
  }
  const inlineContainer =
    status.signInArtifactId === signInSlot?.artifactId ? signInSlot?.container : undefined;

  const copy = browserTakeoverCopy(status);
  let content: ReactNode;
  switch (status.state) {
    case "agent": {
      const { taskId } = status;
      content = (
        <>
          {taskId && !status.signInArtifactId && (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              data-statsig-name="browser-watch-open"
              disabled={watch.isPending}
              onClick={() => openWatch(taskId)}
            >
              {watch.isPending ? "Opening…" : "Watch browser"}
            </Button>
          )}
          {!inlineContainer && status.signInArtifactId && (
            <>
              <Globe className="size-3.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className={status.signInArtifactId ? "font-medium" : "text-muted-foreground"}>
                  {copy.title}
                </p>
                {copy.instruction && (
                  <p className="mt-1 text-muted-foreground">{copy.instruction}</p>
                )}
              </div>
            </>
          )}
          <Button
            type="button"
            size={inlineContainer ? "default" : "xs"}
            variant={status.signInArtifactId ? "primary" : "ghost"}
            data-statsig-name="browser-takeover-take"
            disabled={take.isPending || taskId === undefined}
            onClick={() => {
              if (taskId === undefined) return;
              take.mutate(taskId, {
                onSuccess: (next) => {
                  openWhenGranted.current = next.controlId ?? null;
                },
              });
            }}
          >
            <MousePointerClick className="size-3.5 shrink-0" />
            {copy.takeLabel}
          </Button>
        </>
      );
      break;
    }
    case "stopping": {
      const { controlId } = status;
      content = (
        <>
          <LoaderCircle className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
          <span className="flex-1 text-muted-foreground">
            Pausing okthx so you can use the browser…
          </span>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            data-statsig-name="browser-takeover-withdraw"
            disabled={handBack.isPending || controlId === undefined}
            onClick={() => {
              if (controlId !== undefined) handBack.mutate(controlId);
            }}
          >
            Cancel
          </Button>
        </>
      );
      break;
    }
    case "user": {
      const { controlId, expiresAtMs } = status;
      const nearExpiry = expiresAtMs !== undefined && expiresAtMs - now < EXPIRY_WARNING_MS;
      content = (
        <>
          <MousePointerClick className="size-4 shrink-0 self-start mt-0.5 text-primary" />
          <div className="min-w-0 flex-1" role="status">
            <p className="font-medium">{copy.title}</p>
            <p className="mt-1 text-muted-foreground">{copy.instruction}</p>
            {nearExpiry && expiresAtMs !== undefined && (
              <p className="mt-1 text-destructive">
                Browser closes in{" "}
                {takeoverTimeLeft(expiresAtMs, now).replace(" left", "").toLowerCase()}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="primary"
                data-statsig-name="browser-takeover-open"
                disabled={liveView.isPending || controlId === undefined}
                onClick={() => {
                  if (controlId !== undefined) openBrowser(controlId);
                }}
              >
                {liveView.isPending ? "Opening…" : "Open browser"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                data-statsig-name="browser-takeover-hand-back"
                disabled={handBack.isPending || controlId === undefined}
                onClick={() => {
                  if (controlId !== undefined)
                    handBack.mutate(controlId, { onSuccess: closeBrowser });
                }}
              >
                Continue with okthx
              </Button>
            </div>
          </div>
          <BrowserOptions
            disabled={end.isPending || controlId === undefined}
            onEnd={() => setConfirmingEnd(true)}
          />
        </>
      );
      break;
    }
  }

  const controlId = status.controlId;
  return (
    <div className={cn("pointer-events-auto", !inlineContainer && "flex justify-center mb-2")}>
      {liveViewUrl === null &&
        (inlineContainer ? (
          createPortal(content, inlineContainer)
        ) : (
          <div
            className={cn(
              "flex flex-wrap items-center gap-2 w-[688px] max-w-full text-xs",
              status.state === "agent" && !status.signInArtifactId
                ? "justify-end"
                : "rounded-2xl border bg-popover px-3 py-2",
            )}
          >
            {showSignIn && heldControlId ? (
              <>
                <LockKeyhole aria-hidden className="size-4 text-primary" />
                <span className="min-w-0 flex-1 break-all">
                  Sign in to {credentialRequest?.origin}
                </span>
                <Button
                  type="button"
                  size="sm"
                  data-statsig-name="browser-reopen-sign-in"
                  onClick={() => openBrowser(heldControlId)}
                >
                  Open sign-in
                </Button>
                <BrowserOptions disabled={end.isPending} onEnd={() => setConfirmingEnd(true)} />
              </>
            ) : (
              content
            )}
          </div>
        ))}

      <BrowserControlDialog
        url={liveViewUrl}
        opening={openingTaskId !== undefined && openingTaskId === status.taskId}
        openingFailed={openingFailure !== undefined && openingFailure.taskId === status.taskId}
        signInSheet={
          showSignIn && credentialRequest && taskId && heldControlId ? (
            <BrowserLoginForm
              key={heldControlId}
              origin={credentialRequest.origin}
              hold={{ threadId: thread.id, taskId, controlId: heldControlId }}
              onSaved={() => {
                if (
                  currentHold.current.taskId !== taskId ||
                  currentHold.current.controlId !== heldControlId
                )
                  return;
                closeBrowser();
                void refresh();
              }}
              onManual={() => setManualControlId(heldControlId)}
            />
          ) : undefined
        }
        onRetry={() => {
          setOpenView(null);
          if (heldControlId) openBrowser(heldControlId);
          else if (status.taskId) openWatch(status.taskId);
        }}
        onConnection={(event) => {
          if (status.taskId) connectionLog.mutate({ ...event, taskId: status.taskId });
        }}
        title={copy.title}
        instruction={copy.instruction}
        onClose={closeBrowser}
        onDone={async () => {
          if (showSignIn) return;
          cancelConnection();
          if (!controlId) return;
          const attempt = openAttempt.current;
          await handBack.mutateAsync(controlId);
          if (!status.taskId || attempt !== openAttempt.current) return;
          const url = await watch.mutateAsync(status.taskId);
          if (attempt === openAttempt.current) setOpenView({ taskId: status.taskId, url });
        }}
        mode={
          !showSignIn && heldControlId && openView?.controlId === heldControlId
            ? "control"
            : "watch"
        }
        onTake={async () => {
          if (showSignIn && heldControlId) {
            setManualControlId(heldControlId);
            return;
          }
          cancelConnection();
          if (!status.taskId) return;
          const attempt = openAttempt.current;
          const next = await take.mutateAsync(status.taskId);
          if (attempt !== openAttempt.current) return;
          if (next.state === "user" && next.controlId) openBrowser(next.controlId);
          else openWhenGranted.current = next.controlId ?? null;
        }}
        onRefresh={async (connection) => {
          if (heldControlId && openView?.controlId === heldControlId)
            return await liveView.mutateAsync(
              connection ? { id: heldControlId, connection } : heldControlId,
            );
          if (!status.taskId) throw new Error("Browser unavailable");
          return await watch.mutateAsync(
            connection ? { id: status.taskId, connection } : status.taskId,
          );
        }}
        onEnd={async () => {
          cancelConnection();
          if (controlId) await end.mutateAsync(controlId);
          else if (status.taskId) await endTask.mutateAsync(status.taskId);
          closeBrowser();
        }}
        footer={
          <footer className="flex shrink-0 items-center justify-between gap-2 border-t px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            <BrowserOptions
              disabled={end.isPending || controlId === undefined}
              onEnd={() => setConfirmingEnd(true)}
            />
            <Button
              type="button"
              size="sm"
              variant="primary"
              data-statsig-name="browser-takeover-dialog-hand-back"
              disabled={handBack.isPending || controlId === undefined}
              onClick={() => {
                if (controlId !== undefined)
                  handBack.mutate(controlId, { onSuccess: closeBrowser });
              }}
            >
              Continue with okthx
            </Button>
          </footer>
        }
      />

      <AlertDialog open={confirmingEnd} onOpenChange={setConfirmingEnd}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this request?</AlertDialogTitle>
            <AlertDialogDescription>
              okthx stops here and the browser closes. Anything you already did in it stays done.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-statsig-name="browser-takeover-end-cancel">
              Keep working
            </AlertDialogCancel>
            <AlertDialogAction
              data-statsig-name="browser-takeover-end-confirm"
              disabled={end.isPending || controlId === undefined}
              onClick={() => {
                if (controlId !== undefined) end.mutate(controlId, { onSuccess: closeBrowser });
              }}
            >
              Cancel request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/** Keeps cancellation in an options menu; the parent confirms before ending the request. */
const BrowserOptions = ({ disabled, onEnd }: { disabled: boolean; onEnd: () => void }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="More options"
          data-statsig-name="browser-takeover-options"
          disabled={disabled}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem className="text-destructive" onSelect={onEnd}>
          Cancel request
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
